// src/store/slices/gridShapesSlice.ts
/**
 * Grid Shapes Redux Slice
 * 
 * Manages geometric shapes on the coordinate grid.
 * Phase 3: Points and Basic Shapes
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../types/redux';
import {
  GridShape,
  ShapeType,
  Point2D,
} from '../../types/coordinateGrid';
import {
  calculateArea,
  calculatePerimeter,
} from '../../lib/coordinateGrid/shapeCalculations';

// =============================================================================
// TYPES & INTERFACES
// =============================================================================

export interface GridShapesState {
  shapes: GridShape[];
  selectedShapeIds: string[];
  nextId: number;
  /** Shape currently being drawn (vertices accumulated) */
  drawingShape: {
    isDrawing: boolean;
    type: ShapeType;
    vertices: Point2D[];
  } | null;
  /** Default styling for new shapes */
  defaultStyle: {
    color: string;
    fillColor: string;
    fillOpacity: number;
    showVertexLabels: boolean;
    showArea: boolean;
  };
}

// =============================================================================
// INITIAL STATE
// =============================================================================

const initialState: GridShapesState = {
  shapes: [],
  selectedShapeIds: [],
  nextId: 1,
  drawingShape: null,
  defaultStyle: {
    color: '#3B82F6', // Blue
    fillColor: '#3B82F6',
    fillOpacity: 0.2,
    showVertexLabels: true,
    showArea: false,
  },
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get minimum vertices required for a shape type
 */
function getMinVertices(type: ShapeType): number {
  switch (type) {
    case 'triangle':
      return 3;
    case 'rectangle':
    case 'parallelogram':
      return 4;
    case 'polygon':
      return 3; // Minimum for any polygon
    default:
      return 3;
  }
}

/**
 * Validate if vertices form a valid shape
 */
function validateShape(type: ShapeType, vertices: Point2D[]): boolean {
  const minVertices = getMinVertices(type);
  if (vertices.length < minVertices) {
    return false;
  }

  // Check for duplicate vertices (too close together)
  for (let i = 0; i < vertices.length; i++) {
    const j = (i + 1) % vertices.length;
    const dx = vertices[j].x - vertices[i].x;
    const dy = vertices[j].y - vertices[i].y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    if (distance < 0.01) {
      return false; // Vertices too close
    }
  }

  return true;
}

/**
 * Recalculate shape metrics (area and perimeter)
 */
function recalculateShapeMetrics(shape: GridShape): GridShape {
  if (shape.vertices.length < 3) {
    return {
      ...shape,
      calculatedArea: undefined,
      calculatedPerimeter: undefined,
    };
  }

  return {
    ...shape,
    calculatedArea: calculateArea(shape.vertices),
    calculatedPerimeter: calculatePerimeter(shape.vertices),
  };
}

// =============================================================================
// REDUX SLICE
// =============================================================================

export const gridShapesSlice = createSlice({
  name: 'gridShapes',
  initialState,
  reducers: {
    /**
     * Start drawing a new shape
     */
    startDrawingShape: (state, action: PayloadAction<ShapeType>) => {
      state.drawingShape = {
        isDrawing: true,
        type: action.payload,
        vertices: [],
      };
    },

    /**
     * Add a vertex to the shape being drawn
     */
    addDrawingVertex: (state, action: PayloadAction<Point2D>) => {
      if (!state.drawingShape) {
        return;
      }

      const vertex = action.payload;
      const { type, vertices } = state.drawingShape;

      // Check if we have enough vertices to complete the shape
      const minVertices = getMinVertices(type);
      const canComplete = vertices.length >= minVertices - 1;

      if (canComplete && type !== 'polygon') {
        // For fixed-shape types, complete the shape
        const newVertices = [...vertices, vertex];
        if (validateShape(type, newVertices)) {
          // Complete the shape
          const newShape: GridShape = {
            id: `shape-${state.nextId}`,
            type,
            vertices: newVertices,
            color: state.defaultStyle.color,
            fillColor: state.defaultStyle.fillColor,
            fillOpacity: state.defaultStyle.fillOpacity,
            showVertexLabels: state.defaultStyle.showVertexLabels,
            showCoordinates: false,
            showMeasurements: false,
            showArea: state.defaultStyle.showArea,
            isSelected: false,
          };

          state.shapes.push(recalculateShapeMetrics(newShape));
          state.nextId++;
          state.drawingShape = null;
        }
      } else {
        // Add vertex and continue drawing
        state.drawingShape.vertices.push(vertex);
      }
    },

    /**
     * Complete the current shape being drawn
     */
    completeShape: (state) => {
      if (!state.drawingShape || state.drawingShape.vertices.length < 2) {
        state.drawingShape = null;
        return;
      }

      const { type, vertices } = state.drawingShape;

      // For polygon, we need at least 3 vertices
      if (type === 'polygon' && vertices.length < 3) {
        state.drawingShape = null;
        return;
      }

      if (validateShape(type, vertices)) {
        const newShape: GridShape = {
          id: `shape-${state.nextId}`,
          type,
          vertices: [...vertices],
          color: state.defaultStyle.color,
          fillColor: state.defaultStyle.fillColor,
          fillOpacity: state.defaultStyle.fillOpacity,
          showVertexLabels: state.defaultStyle.showVertexLabels,
          showCoordinates: false,
          showMeasurements: false,
          showArea: state.defaultStyle.showArea,
          isSelected: false,
        };

        state.shapes.push(recalculateMetrics(newShape));
        state.nextId++;
      }

      state.drawingShape = null;
    },

    /**
     * Cancel drawing the current shape
     */
    cancelDrawing: (state) => {
      state.drawingShape = null;
    },

    /**
     * Add a complete shape (for programmatic creation)
     */
    addShape: (
      state,
      action: PayloadAction<{
        type: ShapeType;
        vertices: Point2D[];
        style?: Partial<GridShape>;
      }>
    ) => {
      const { type, vertices, style = {} } = action.payload;

      if (!validateShape(type, vertices)) {
        return;
      }

      const newShape: GridShape = {
        id: `shape-${state.nextId}`,
        type,
        vertices: [...vertices],
        color: state.defaultStyle.color,
        fillColor: state.defaultStyle.fillColor,
        fillOpacity: state.defaultStyle.fillOpacity,
        showVertexLabels: state.defaultStyle.showVertexLabels,
        showCoordinates: false,
        showMeasurements: false,
        showArea: state.defaultStyle.showArea,
        isSelected: false,
        ...style,
      };

      state.shapes.push(recalculateMetrics(newShape));
      state.nextId++;
    },

    /**
     * Update an existing shape
     */
    updateShape: (
      state,
      action: PayloadAction<{
        id: string;
        updates: Partial<GridShape>;
      }>
    ) => {
      const shape = state.shapes.find((s) => s.id === action.payload.id);
      if (shape) {
        Object.assign(shape, action.payload.updates);

        // Recalculate metrics if vertices changed
        if (action.payload.updates.vertices) {
          const updated = recalculateShapeMetrics(shape);
          Object.assign(shape, updated);
        }
      }
    },

    /**
     * Delete a shape by ID
     */
    deleteShape: (state, action: PayloadAction<string>) => {
      state.shapes = state.shapes.filter((s) => s.id !== action.payload);
      state.selectedShapeIds = state.selectedShapeIds.filter((id) => id !== action.payload);
    },

    /**
     * Select a shape (with optional multi-select via Ctrl key)
     */
    selectShape: (
      state,
      action: PayloadAction<{
        id: string;
        ctrlKey?: boolean;
      }>
    ) => {
      const { id, ctrlKey = false } = action.payload;

      if (!ctrlKey) {
        // Deselect all shapes
        state.shapes.forEach((s) => {
          s.isSelected = false;
        });
        state.selectedShapeIds = [];
      }

      const shape = state.shapes.find((s) => s.id === id);
      if (shape) {
        shape.isSelected = !shape.isSelected;
        if (shape.isSelected) {
          state.selectedShapeIds.push(id);
        } else {
          state.selectedShapeIds = state.selectedShapeIds.filter((sid) => sid !== id);
        }
      }
    },

    /**
     * Toggle area display for a shape
     */
    toggleShowArea: (state, action: PayloadAction<string>) => {
      const shape = state.shapes.find((s) => s.id === action.payload);
      if (shape) {
        shape.showArea = !shape.showArea;
      }
    },

    /**
     * Toggle measurements display for a shape
     */
    toggleShowMeasurements: (state, action: PayloadAction<string>) => {
      const shape = state.shapes.find((s) => s.id === action.payload);
      if (shape) {
        shape.showMeasurements = !shape.showMeasurements;
      }
    },

    /**
     * Recalculate metrics for a shape
     */
    recalculateMetrics: (state, action: PayloadAction<string>) => {
      const shape = state.shapes.find((s) => s.id === action.payload);
      if (shape) {
        const updated = recalculateShapeMetrics(shape);
        Object.assign(shape, updated);
      }
    },

    /**
     * Set default shape style
     */
    setDefaultStyle: (
      state,
      action: PayloadAction<Partial<GridShapesState['defaultStyle']>>
    ) => {
      state.defaultStyle = {
        ...state.defaultStyle,
        ...action.payload,
      };
    },

    /**
     * Load shapes state (for scene loading)
     */
    loadShapesState: (state, action: PayloadAction<Partial<GridShapesState>>) => {
      Object.assign(state, { ...initialState, ...action.payload });
    },

    /**
     * Clear all shapes
     */
    clearAllShapes: (state) => {
      state.shapes = [];
      state.selectedShapeIds = [];
      state.drawingShape = null;
    },
  },
});

// Export actions
export const {
  startDrawingShape,
  addDrawingVertex,
  completeShape,
  cancelDrawing,
  addShape,
  updateShape,
  deleteShape,
  selectShape,
  toggleShowArea,
  toggleShowMeasurements,
  recalculateMetrics,
  setDefaultStyle,
  loadShapesState,
  clearAllShapes,
} = gridShapesSlice.actions;

// Selectors
export const selectAllShapes = (state: RootState) => state.gridShapes.shapes;
export const selectSelectedShapes = (state: RootState) =>
  state.gridShapes.shapes.filter((s) => s.isSelected);
export const selectDrawingShape = (state: RootState) => state.gridShapes.drawingShape;
export const selectShapeById = (id: string) => (state: RootState) =>
  state.gridShapes.shapes.find((s) => s.id === id);

export default gridShapesSlice.reducer;

